import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ethers } from "ethers";
import { CHAINS } from "../config/chains";
import { FALLBACK_GAS_PER_TX } from "../utils/gasEstimation";
import { checkTempoFeeTokenBalance } from "../utils/tempoFeeTokens";

const ERC20_BALANCE_ABI = ["function balanceOf(address) view returns (uint256)"];

// Background re-check while the onboarding "Add funds" step is open, so its
// rows flip to ready on their own once a transfer lands.
const REFRESH_MS = 15000;

/** Minimum deposit-token balance before onboarding treats the wallet as funded. */
export const MIN_FUNDING_TOKEN = 0.01;

/**
 * Minimum native gas balance (~1 confidential transfer at the fallback fee).
 * Avoids marking dust as ready when the UI would round it to 0.0000.
 */
export const MIN_FUNDING_GAS = FALLBACK_GAS_PER_TX;

const UNKNOWN = { amount: null, ok: null };

const isZeroAddress = (address) => !address || /^0x0+$/i.test(address);

/** Identifies which wallet/chain/token a funding read belongs to. */
export const fundingSelectionKey = (account, chainKey, tokenAddress) =>
  `${account || ""}|${chainKey || ""}|${tokenAddress || ""}`.toLowerCase();

/**
 * Public wallet balances the onboarding needs before account creation:
 * something to pay gas with, and the stablecoin the user will deposit.
 * Reads go through the selected chain's own RPC, same as useBalances — the
 * wallet can still be pointed at another network at this point.
 *
 * gas / token are { amount: number|null, ok: boolean|null }; ok is null when
 * the read failed, so the UI can say "couldn't check" rather than "needed".
 * ok is true only above a small floor (MIN_FUNDING_TOKEN / MIN_FUNDING_GAS),
 * not merely non-zero — dust that rounds to 0.00 in the UI stays underfunded.
 * Results are tagged with the selection they were read for, and hidden once
 * the selection changes, so a slow read can never describe the wrong chain.
 */
export function useWalletFunding({ account, chainKey, token, enabled = true }) {
  const chain = chainKey ? CHAINS[chainKey] : null;
  const tokenAddress = token?.address || null;
  const tokenDecimals = token?.decimals ?? 6;
  const selectionKey = fundingSelectionKey(account, chainKey, tokenAddress);

  const [result, setResult] = useState(null); // { key, gas, token }
  const [checking, setChecking] = useState(false);
  const requestIdRef = useRef(0);

  const readProvider = useMemo(
    () =>
      chain?.rpcUrl
        ? new ethers.providers.StaticJsonRpcProvider(chain.rpcUrl)
        : null,
    [chain],
  );

  const refresh = useCallback(async () => {
    if (!account || !readProvider) return null;
    const key = selectionKey;
    const requestId = (requestIdRef.current += 1);
    setChecking(true);

    const readGas = async () => {
      try {
        // Tempo pays fees in stablecoin fee tokens, not a native coin.
        if (chainKey === "tempo") {
          const info = await checkTempoFeeTokenBalance(readProvider, account);
          const amount = parseFloat(info.feeTokenBalance) || 0;
          return {
            amount,
            // Fee tokens are stablecoins; reuse the deposit floor so dust
            // doesn't look "ready" at 0.00 display precision.
            ok: info.hasFeeToken && amount >= MIN_FUNDING_TOKEN,
            symbol: info.feeTokenSymbol,
          };
        }
        const raw = await readProvider.getBalance(account);
        const amount = parseFloat(ethers.utils.formatEther(raw));
        return { amount, ok: amount >= MIN_FUNDING_GAS };
      } catch (error) {
        console.warn("[useWalletFunding] gas balance read failed:", error?.message || error);
        return UNKNOWN;
      }
    };

    const readToken = async () => {
      if (isZeroAddress(tokenAddress)) return UNKNOWN;
      try {
        const erc20 = new ethers.Contract(tokenAddress, ERC20_BALANCE_ABI, readProvider);
        const raw = await erc20.balanceOf(account);
        const amount = parseFloat(ethers.utils.formatUnits(raw, tokenDecimals));
        return {
          amount,
          ok: amount >= MIN_FUNDING_TOKEN,
        };
      } catch (error) {
        console.warn("[useWalletFunding] token balance read failed:", error?.message || error);
        return UNKNOWN;
      }
    };

    const [gas, tokenBalance] = await Promise.all([readGas(), readToken()]);
    const next = { key, gas, token: tokenBalance };
    // A newer read already started — let that one own the state.
    if (requestId === requestIdRef.current) {
      setResult(next);
      setChecking(false);
    }
    return next;
  }, [account, chainKey, readProvider, selectionKey, tokenAddress, tokenDecimals]);

  useEffect(() => {
    if (!enabled || !account || !readProvider) return undefined;
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [enabled, account, readProvider, refresh]);

  const checked = result?.key === selectionKey;
  return {
    selectionKey,
    checked,
    checking,
    gas: checked ? result.gas : null,
    token: checked ? result.token : null,
    refresh,
  };
}

export default useWalletFunding;
